import { z } from 'zod';
import { RecordIdSchema } from './identifiers';
import { DecimalStringSchema, MoneySchema } from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Customer invoices, credit notes and debit notes — `COL-INVOICE-001/002`, `FIN-TAX-001` (BMP-2
 * package 2).
 *
 * **No tax rule is invented.** A line's tax comes only from a tax code in the deployment's reference
 * data (`taxCodes`) with a rate in force on the issue date; a code with no rate in force is refused
 * (`TAX_RATE_NOT_CONFIGURED`), and a line with no tax code carries no tax. Which taxes apply is `BD-16`
 * and the appointed accountant's (`SD-08`).
 *
 * **Not Egyptian e-invoicing.** Nothing is submitted to the Tax Authority; whether the client must is
 * `BD-53`, and the adapter is BMP-3. `eInvoice.state` says so on every document.
 */

export const INVOICE_KINDS = ['invoice', 'creditNote', 'debitNote'] as const;
export const InvoiceKindSchema = z.enum(INVOICE_KINDS);
export type InvoiceKind = z.infer<typeof InvoiceKindSchema>;

/**
 * `cancelled` is reached only through credit notes that credit the whole invoice; a draft is
 * `discarded`. An issued document is never edited (ADR-0009).
 */
export const INVOICE_STATES = [
  'draft',
  'pendingApproval',
  'issued',
  'cancelled',
  'discarded',
] as const;
export const InvoiceStateSchema = z.enum(INVOICE_STATES);
export type InvoiceState = z.infer<typeof InvoiceStateSchema>;

/** A quantity: positive, at most four decimals, never binary floating point. */
export const QuantitySchema = DecimalStringSchema.refine(
  (value) => !value.startsWith('-') && !/^0(\.0+)?$/.test(value) && !/\.\d{5,}$/.test(value),
  { message: 'QUANTITY_EXPECTED' },
);

export const InvoiceLineInputSchema = z.strictObject({
  description: z.string().trim().min(2).max(300),
  quantity: QuantitySchema,
  unitPrice: DecimalStringSchema,
  discount: DecimalStringSchema.optional(),
  /** A code from the `taxCodes` reference list; omitted means no tax on the line. */
  taxCode: z.string().trim().min(1).max(40).optional(),
});
export type InvoiceLineInput = z.infer<typeof InvoiceLineInputSchema>;

export const InvoiceLineSchema = z.strictObject({
  lineNo: z.number().int().positive(),
  description: z.string(),
  quantity: DecimalStringSchema,
  unitPrice: DecimalStringSchema,
  discount: DecimalStringSchema,
  taxCode: z.string().optional(),
  /** The rate in force on the issue date, snapshotted when the document is issued. */
  taxRatePercent: DecimalStringSchema,
  net: DecimalStringSchema,
  tax: DecimalStringSchema,
  gross: DecimalStringSchema,
});
export type InvoiceLine = z.infer<typeof InvoiceLineSchema>;

export const InvoiceSchema = z.strictObject({
  invoiceId: RecordIdSchema,
  kind: InvoiceKindSchema,
  number: z.string().optional(),
  /** For a credit or debit note: the issued invoice it adjusts. */
  relatesToInvoiceId: RecordIdSchema.optional(),
  customerId: RecordIdSchema,
  contractId: RecordIdSchema.optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  issueDate: BusinessDateSchema,
  dueDate: BusinessDateSchema.optional(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  lines: z.array(InvoiceLineSchema),
  totals: z.strictObject({ net: MoneySchema, tax: MoneySchema, gross: MoneySchema }),
  /** For an invoice: what its issued credit notes have credited so far. */
  creditedAmount: MoneySchema.optional(),
  notes: z.string().optional(),
  state: InvoiceStateSchema,
  approvalRequestId: z.string().optional(),
  eInvoice: z.strictObject({ state: z.literal('notSubmitted'), decision: z.literal('BD-53') }),
  createdByAccountId: z.string(),
  issuedByAccountId: z.string().optional(),
  issuedAt: InstantSchema.optional(),
  discardReason: z.string().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Invoice = z.infer<typeof InvoiceSchema>;

const IdempotencyKeySchema = z.string().trim().min(8).max(200);

export const CreateInvoiceSchema = z.strictObject({
  kind: InvoiceKindSchema.default('invoice'),
  relatesToInvoiceId: RecordIdSchema.optional(),
  customerId: RecordIdSchema,
  contractId: RecordIdSchema.optional(),
  branchId: RecordIdSchema,
  issueDate: BusinessDateSchema,
  dueDate: BusinessDateSchema.optional(),
  lines: z.array(InvoiceLineInputSchema).min(1).max(100),
  notes: z.string().trim().max(1000).optional(),
  idempotencyKey: IdempotencyKeySchema,
});
export type CreateInvoice = z.infer<typeof CreateInvoiceSchema>;

export const UpdateInvoiceSchema = z.strictObject({
  issueDate: BusinessDateSchema.optional(),
  dueDate: BusinessDateSchema.optional(),
  lines: z.array(InvoiceLineInputSchema).min(1).max(100).optional(),
  notes: z.string().trim().max(1000).optional(),
  expectedVersion: z.number().int().positive(),
});
export type UpdateInvoice = z.infer<typeof UpdateInvoiceSchema>;

export const IssueInvoiceSchema = z.strictObject({ expectedVersion: z.number().int().positive() });
export const DiscardInvoiceSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

export const InvoiceQuerySchema = z.strictObject({
  kind: InvoiceKindSchema.optional(),
  state: InvoiceStateSchema.optional(),
  customerId: RecordIdSchema.optional(),
  contractId: RecordIdSchema.optional(),
  relatesToInvoiceId: RecordIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().min(1).max(200).optional(),
});
export type InvoiceQuery = z.infer<typeof InvoiceQuerySchema>;

export const InvoicePageSchema = z.strictObject({
  items: z.array(InvoiceSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  nextCursor: z.string().optional(),
});
export type InvoicePage = z.infer<typeof InvoicePageSchema>;

export const INVOICE_APPROVAL_OPERATIONS = { issue: 'finance.invoice.issue' } as const;

export const INVOICE_AUDIT_ACTIONS = {
  drafted: 'finance.invoice.drafted',
  updated: 'finance.invoice.updated',
  discarded: 'finance.invoice.discarded',
  submitted: 'finance.invoice.submitted',
  issued: 'finance.invoice.issued',
  refused: 'finance.invoice.refused',
  cancelled: 'finance.invoice.cancelled',
} as const;
